import React from 'react';
import BibleTextVerse from '../components/BibleTextVerse/BibleTextVerse';
import { BibleBookNames, BibleBookNamesType } from '../data/BibleBookNames';
import { BibleVersionsValues } from '../data/BibleVersions';
import { BibleVersification } from '../data/BibleVersification';
import IBibleJson from '../interfaces/IBibleJson';
import IBibleVerse from '../interfaces/IBibleVerse';
import IBibleReference from '../interfaces/IBibleReference';

type referenceOptions = 'none' | 'before' | 'after';

interface IBibleTextRequest {
  bibleTextPromise: Promise<JSX.Element[]>,
  cancel: () => void
}

interface IBibleVerseRequest {
  versePromise: Promise<IBibleVerse>,
  cancel: () => void
}

export interface IBibleTextService {
    getBookText: (verseReference: string, 
      version: string, 
      refLocation?: referenceOptions, 
      titles?: boolean, 
      chapters?: boolean, 
      paragraphs?: boolean, 
      openExtraOptions?: Function,
      setReference?: Function) =>  IBibleTextRequest,
    getBookJson: (book: string, version: string, signal: AbortSignal) =>  Promise<IBibleJson>,
    convertToFullBookName: (reference: string) => string,
    getVerseText: (verseReference: string, 
      version: BibleVersionsValues, 
      refLocation?: referenceOptions) => Promise<IBibleVerseRequest>,
    parseBibleReference: (verseReference: string) => IBibleReference,
}

const BibleTextService: IBibleTextService = {
    convertToFullBookName: (reference) => {
      const book: BibleBookNamesType = reference.slice(0, 3).toUpperCase() as BibleBookNamesType;
      const chapterVerse: string = reference.slice(3);

      return BibleBookNames[book] + chapterVerse;
    },

    getBookJson: (book, version, signal) => {
      const fetchUrl = `/assets/bibles/${version}/${book}.json`; 

      return fetch(
        fetchUrl,
        { signal,
          headers : { 
            'Content-Type': 'application/json',
            'Accept': 'application/json'
           }
        })
          .then((response) =>{
            return response.json();
          })
          .then( async(bookJson) =>{
            return bookJson;
          })
          .catch(error => {
            console.error(error);
            return {chapters:[]};
          });
    },

    getVerseText: async (verseReference: string, 
      version: BibleVersionsValues, 
      refLocation='none') => {
        
        const book = verseReference.slice(0, 3).toUpperCase() as BibleBookNamesType
        const chapterMatch = verseReference.match(/([\d|\w]\w\w )\d{1,3}/);
        const chapter = chapterMatch ? +chapterMatch[0].replace(/([\d|\w]\w\w )/, '') : 1;
        const verseMatch = verseReference.match(/([\d|\w]\w\w \d{1,3}:)\d{1,3}/);
        const verse = verseMatch ? +verseMatch[0].replace(/([\d|\w]\w\w \d{1,3}:)/, '') : 1;

        const controller = new AbortController();
        const signal = controller.signal;
        const cancel = () => { controller.abort() };
        
        const versePromise = BibleTextService.getBookJson(book, version, signal)
          .then((bookJson: IBibleJson) => {
            let verseText = '';
            bookJson.chapters[chapter][verse].forEach(verseContent => {
              if(verseContent.t === 'vt') {
                verseText = verseText + verseContent.c + ' ';
              }
            })

            const bibleVerse: IBibleVerse = {
              reference: verseReference,
              displayReference: `${BibleBookNames[book]} ${chapter}:${verse}`,
              book: BibleBookNames[book],
              chapter,
              verse,
              version,
              text: verseText
            };
    
            return bibleVerse;
          });

        return { versePromise, cancel };
    },
    
    getBookText: (verseReference: string, 
      version: string, 
      refLocation='none', 
      titles=true, 
      chapters=true, 
      paragraphs=true,
      openExtraOptions?: Function,
      setReference?: Function) => {

      const controller = new AbortController();
      const signal = controller.signal;
      const cancel = () => { controller.abort() };
      const bibleText: JSX.Element[] = [];

      const book: BibleBookNamesType = verseReference.slice(0, 3).toUpperCase() as BibleBookNamesType;
      const chapterVerseReference = verseReference.substring(3);
      const bibleTextPromise = BibleTextService.getBookJson(book, version, signal).then((bookJson: IBibleJson) => {
        const startChapterMatch = verseReference.match(/([\d|\w]\w\w )\d{1,3}/);
        const startChapter = startChapterMatch ? +startChapterMatch[0].replace(/([\d|\w]\w\w )/, '') : 1;
        const startVerseMatch = verseReference.match(/([\d|\w]\w\w \d{1,3}:)\d{1,3}/);
        const startVerse = startVerseMatch ? +startVerseMatch[0].replace(/([\d|\w]\w\w \d{1,3}:)/, '') : 1;
        const endChapterMatch = verseReference.match(/([\d|\w]\w\w \d{1,3}:\d{1,3}-)\d{1,3}(?=:)/);
        const endChapter = endChapterMatch ? +endChapterMatch[0].replace(/([\d|\w]\w\w \d{1,3}:\d{1,3}-)/, '') : startChapter;
        const endVerseMatch = verseReference.match(/([\d|\w]\w\w \d{1,3}:\d{1,3}-\d{1,3}:)\d{1,3}/);
        let endVerse = endVerseMatch ? +endVerseMatch[0].replace(/([\d|\w]\w\w \d{1,3}:\d{1,3}-\d{1,3}:)/, '') : false;
        const endVerseMatchAlt = verseReference.match(/([\d|\w]\w\w \d{1,3}:\d{1,3}-)\d{1,3}/);
        endVerse = endVerse ? endVerse :
          endVerseMatchAlt ? +endVerseMatchAlt[0].replace(/([\d|\w]\w\w \d{1,3}:\d{1,3}-)/, '') : startVerse;

        if(startChapter === endChapter && verseReference.indexOf(':') === -1) {
          endVerse = BibleVersification[book][startChapter];
        }

        if(!book || !chapterVerseReference || !bookJson.chapters.length || (!startChapter && !startVerse))
        {
          bibleText.push(<p><b>{BibleBookNames[book]} {chapterVerseReference} {version.toUpperCase()}</b><br />Error: Please try a different version.</p>);
          return bibleText;
        }

        if(refLocation === 'before') {
          bibleText.push(<h2 className='bible-text-reference'>{BibleBookNames[book]} {chapterVerseReference}</h2>);
        }
    
        for (let chapterIndex = startChapter; chapterIndex <= endChapter; chapterIndex++) {
          const firstChapterVerse = chapterIndex === startChapter ? startVerse : 1;
    
          // length - 1 because chapterIndex 0 is an empty placeholder
          const lastChapterVerse = chapterIndex === endChapter ? endVerse : bookJson.chapters[chapterIndex].length - 1;
    
          for (let verseIndex = firstChapterVerse; verseIndex <= lastChapterVerse; verseIndex++) {
            let showVerseNumber = true;
            
            bookJson.chapters[chapterIndex][verseIndex].forEach(element => {
              switch (element.t) {
                case 's':
                  if(titles) 
                    { bibleText.push(<h3 className='bible-text-subtitle'>{element.c}</h3>); }
                  break;
                case 'vt':
                  if(showVerseNumber) {
                    if(verseIndex === 1) {
                      if (chapters) {
                        bibleText.push(<b className='chapter-num'>{chapterIndex.toString()}</b>);
                      }
                    }
                    // add property to this verse number to make it selectable
                    bibleText.push(<span className='verse-num'>{verseIndex.toString()} </span>);
                    showVerseNumber = false;
                  }
                  // add same property every verse text fragment to make it selectable.
                  bibleText.push(<BibleTextVerse book={book} chapter={chapterIndex} verse={verseIndex} text={element.c} openExtraOptions={openExtraOptions} setReference={setReference} />);
                  break;
                case 'lb':
                  if(paragraphs) {
                    bibleText.push(<br />);
                  }
                  break;
                case 'p':
                  if(paragraphs) {
                    bibleText.push(<p></p>);
                  }
                  break;
                default:
                  break;
              }
            })
            
          }
        }
        return bibleText;

      }).catch(error => {
        console.error(error);
        return bibleText;
      });
      
      return { bibleTextPromise, cancel };
    },

    parseBibleReference(reference) {

      if(reference) {
        const book: BibleBookNamesType = reference.slice(0, 3).toUpperCase() as BibleBookNamesType;
        const fullBookName = BibleBookNames[book];
        const startChapterMatch = reference.match(/([\d|\w]\w\w )\d{1,3}/);
        const startChapter = startChapterMatch ? +startChapterMatch[0].replace(/([\d|\w]\w\w )/, '') : 1;
        const startVerseMatch = reference.match(/([\d|\w]\w\w \d{1,3}:)\d{1,3}/);
        const startVerse = startVerseMatch ? +startVerseMatch[0].replace(/([\d|\w]\w\w \d{1,3}:)/, '') : 1;
        const endChapterMatch = reference.match(/([\d|\w]\w\w \d{1,3}:\d{1,3}-)\d{1,3}(?=:)/);
        const endChapter = endChapterMatch ? +endChapterMatch[0].replace(/([\d|\w]\w\w \d{1,3}:\d{1,3}-)/, '') : startChapter;
        const endVerseMatch = reference.match(/([\d|\w]\w\w \d{1,3}:\d{1,3}-\d{1,3}:)\d{1,3}/);
        let endVerse = endVerseMatch ? +endVerseMatch[0].replace(/([\d|\w]\w\w \d{1,3}:\d{1,3}-\d{1,3}:)/, '') : false;
        const endVerseMatchAlt = reference.match(/([\d|\w]\w\w \d{1,3}:\d{1,3}-)\d{1,3}/);
        endVerse = endVerse ? endVerse :
          endVerseMatchAlt ? +endVerseMatchAlt[0].replace(/([\d|\w]\w\w \d{1,3}:\d{1,3}-)/, '') : startVerse;

        if(startChapter === endChapter && reference.indexOf(':') === -1) {
          endVerse = BibleVersification[book][startChapter];
        }

        let bibleReference: IBibleReference = {
          reference,
          book,
          fullBookName,
          startBookChapter: `${book} ${startChapter}`,
          startBookChapterVerse: `${book} ${startChapter}:${startVerse}`,
          endBookChapterVerse: `${book} ${endChapter}:${endVerse}`,
          startChapter,
          endChapter,
          startVerse,
          endVerse,
        }
  
        if(!book || !startChapter)
        {
          console.error("bible verse parse error")
        } 
  
        return bibleReference;
      } else {
        const bibleReference: IBibleReference = {
          reference,
          book: BibleBookNames.GEN as BibleBookNamesType,
          fullBookName: '',
          startBookChapter:  '',
          startBookChapterVerse:  '',
          endBookChapterVerse:  '',
          startChapter: 0,
          endChapter: 0,
          startVerse: 0,
          endVerse: 0,
        }
        console.error("Undefined verse reference: passage can't be parsed.")
        return bibleReference;
      }
    },
}

export default BibleTextService;